import { useState } from 'react';
import { X } from 'lucide-react';
import { MEASUREMENT_SITES, cleanMeasurements, upsertMeasurement } from '../logic/measurements';
import { FONT_BODY, FONT_MONO } from '../theme';
import { Button, Input } from '../ui';

/* Tape measurements for one date (state.measurements, keyed by date). Empty
 * fields are removed; the last value of each site shows as the placeholder. */
export function MeasureSheet({ t, state, setState, dayKey, onClose }) {
  const rows = cleanMeasurements(state.measurements);
  const current = rows.find(m => m.date === dayKey) || {};
  const lastOf = id => [...rows].reverse().find(m => m.date < dayKey && m[id] != null)?.[id];
  const [draft, setDraft] = useState(() => {
    const d = {};
    for (const s of MEASUREMENT_SITES) d[s.id] = current[s.id] != null ? String(current[s.id]) : '';
    d.note = current.note || '';
    return d;
  });
  const [hintFor, setHintFor] = useState(null);

  const save = () => {
    const patch = { note: draft.note };
    for (const s of MEASUREMENT_SITES) {
      const v = parseFloat(String(draft[s.id]).replace(',', '.'));
      patch[s.id] = Number.isFinite(v) && v >= s.min && v <= s.max ? Math.round(v * 10) / 10 : null;
    }
    setState(prev => ({ ...prev, measurements: upsertMeasurement(prev.measurements, dayKey, patch) }));
    onClose();
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    }}>
      <div role="dialog" aria-label="Measurements" onClick={e => e.stopPropagation()} style={{
        background: t.cardBg, borderTopLeftRadius: 16, borderTopRightRadius: 16, width: '100%', maxWidth: 560,
        maxHeight: '88vh', display: 'flex', flexDirection: 'column', fontFamily: FONT_BODY,
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', padding: '10px 8px 8px 18px', borderBottom: `1px solid ${t.divider}` }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: t.text }}>Measurements</div>
            <div style={{ fontSize: 12, color: t.textMuted }}>
              {new Date(dayKey).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} · cm · same spot, same time of day
            </div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{
            width: 44, height: 44, background: 'transparent', border: 'none', color: t.textMuted, cursor: 'pointer',
          }}><X size={18} /></button>
        </div>
        <div style={{ overflowY: 'auto', padding: '8px 18px', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {MEASUREMENT_SITES.map(s => {
            const last = lastOf(s.id);
            return (
              <div key={s.id}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 48 }}>
                  <button type="button" onClick={() => setHintFor(h => (h === s.id ? null : s.id))} style={{
                    flex: 1, textAlign: 'left', background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
                    fontFamily: FONT_BODY,
                  }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: t.text }}>{s.label}</span>
                    <span style={{ fontSize: 11, color: t.textFaint, marginLeft: 6 }}>{hintFor === s.id ? '▴' : 'how?'}</span>
                  </button>
                  <Input t={t} inputMode="decimal" aria-label={`${s.label} in cm`} value={draft[s.id]}
                    placeholder={last != null ? String(last) : '—'}
                    onChange={e => setDraft(d => ({ ...d, [s.id]: e.target.value }))}
                    style={{ width: 96, textAlign: 'right', fontFamily: FONT_MONO, height: 40 }} />
                </div>
                {hintFor === s.id && <div style={{ fontSize: 12, color: t.textMuted, paddingBottom: 6 }}>{s.hint}</div>}
              </div>
            );
          })}
          <Input t={t} value={draft.note} maxLength={120} placeholder="Note (optional)"
            onChange={e => setDraft(d => ({ ...d, note: e.target.value }))} style={{ marginTop: 6 }} />
        </div>
        <div style={{ padding: '10px 18px 14px', borderTop: `1px solid ${t.divider}` }}>
          <Button t={t} variant="primary" size="lg" onClick={save} style={{ width: '100%', minHeight: 48, justifyContent: 'center' }}>
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}
